import { useEffect, useMemo, useState } from 'react';
import { featureSettingsApi } from '../lib/api';

export interface StructuredAddress {
  formatted: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  zip?: string;
  city: string;
  state?: string;
  country?: string;
  placeId?: string;
  provider?: 'google_places' | 'geoapify';
  lat?: number;
  lon?: number;
}

export interface GeoapifySuggestion {
  id: string;
  label: string;
  structured: StructuredAddress;
}

export interface UseGeoapifyAddressAutocompleteArgs {
  query: string;
  enabled?: boolean;
  limit?: number;
  lang?: string;
  debounceMs?: number;
}

type ParsedAddress = {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
};

const splitStreetAndHouseNumber = (line: string): { street: string; houseNumber: string } => {
  const trimmed = String(line || '').trim();
  if (!trimmed) return { street: '', houseNumber: '' };
  const match = trimmed.match(/^(.*?)[,\s]+(\d+\S*)$/);
  if (!match) return { street: trimmed, houseNumber: '' };
  return {
    street: String(match[1] || '').trim(),
    houseNumber: String(match[2] || '').trim(),
  };
};

const parseAddressFromFormatted = (formatted: string): ParsedAddress => {
  const text = String(formatted || '').trim();
  if (!text) {
    return { street: '', houseNumber: '', postalCode: '', city: '', country: '' };
  }
  const parts = text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const line1 = parts[0] || '';
  const line2 = parts[1] || '';
  const line3 = parts[2] || '';
  const { street, houseNumber } = splitStreetAndHouseNumber(line1);
  const zipCityMatch = line2.match(/^(\d{4,6})\s+(.+)$/);
  const postalCode = zipCityMatch ? String(zipCityMatch[1] || '').trim() : '';
  const city = zipCityMatch ? String(zipCityMatch[2] || '').trim() : line2;
  const country = line3 || '';
  return { street, houseNumber, postalCode, city, country };
};

let cachedGooglePlacesApiKeyFromSettings: string | null = null;
let googlePlacesApiKeyPromise: Promise<string> | null = null;

const loadGooglePlacesApiKeyFromSettings = async (): Promise<string> => {
  if (cachedGooglePlacesApiKeyFromSettings !== null) {
    return cachedGooglePlacesApiKeyFromSettings;
  }
  if (!googlePlacesApiKeyPromise) {
    googlePlacesApiKeyPromise = (async () => {
      try {
        const settings = await featureSettingsApi.get();
        const key = String(settings.googlePlacesApiKey || '').trim();
        // „***“ ist die Anzeige eines gesetzten Schlüssels, kein Schlüssel (T-0029).
        cachedGooglePlacesApiKeyFromSettings = /[A-Za-z0-9]/.test(key) ? key : '';
      } catch {
        cachedGooglePlacesApiKeyFromSettings = '';
      } finally {
        googlePlacesApiKeyPromise = null;
      }
      return cachedGooglePlacesApiKeyFromSettings;
    })();
  }
  return googlePlacesApiKeyPromise;
};

export const clearAddressAutocompleteSettingsCache = () => {
  cachedGooglePlacesApiKeyFromSettings = null;
  googlePlacesApiKeyPromise = null;
};

export function useGeoapifyAddressAutocomplete({
  query,
  enabled = true,
  limit = 5,
  lang = 'de',
  debounceMs = 300,
}: UseGeoapifyAddressAutocompleteArgs) {
  const [suggestions, setSuggestions] = useState<GeoapifySuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  /** Der Anbieter hat abgelehnt — meist ein ungültiger Schlüssel. */
  const [providerFailed, setProviderFailed] = useState(false);
  const [settingsGoogleApiKey, setSettingsGoogleApiKey] = useState<string | null>(cachedGooglePlacesApiKeyFromSettings);

  const envGoogleApiKey =
    ((import.meta as any).env?.VITE_GOOGLE_PLACES_API_KEY as string | undefined) ||
    ((globalThis as any).__VITE_GOOGLE_PLACES_API_KEY as string | undefined) ||
    ((typeof process !== 'undefined' ? (process as any).env?.VITE_GOOGLE_PLACES_API_KEY : undefined) as string | undefined);
  const googleApiKey = useMemo(() => {
    const fromSettings = String(settingsGoogleApiKey || '').trim();
    if (fromSettings !== '') return fromSettings;
    return String(envGoogleApiKey || '').trim();
  }, [settingsGoogleApiKey, envGoogleApiKey]);
  const geoapifyApiKey =
    ((import.meta as any).env?.VITE_GEOAPIFY_KEY as string | undefined) ||
    ((globalThis as any).__VITE_GEOAPIFY_KEY as string | undefined) ||
    // Fallback for non-Vite runtimes (tests/Node)
    ((typeof process !== 'undefined' ? (process as any).env?.VITE_GEOAPIFY_KEY : undefined) as string | undefined);
  const trimmed = useMemo(() => (query || '').trim(), [query]);

  useEffect(() => {
    let active = true;
    if (!enabled || settingsGoogleApiKey !== null || trimmed.length < 3) return;
    void loadGooglePlacesApiKeyFromSettings().then((key) => {
      if (!active) return;
      setSettingsGoogleApiKey(key);
    });
    return () => {
      active = false;
    };
  }, [enabled, settingsGoogleApiKey, trimmed]);

  useEffect(() => {
    const controller = new AbortController();

    if (!enabled || (!googleApiKey && !geoapifyApiKey) || trimmed.length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    const timeout = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        // Ein unbrauchbarer Google-Schlüssel darf die Vervollständigung nicht
        // abschalten: Vorher lief jede Suche in den Google-Zweig, Google lehnte
        // ab, und der funktionierende Geoapify-Zweig wurde nie erreicht — das
        // Feld blieb wortlos leer (T-0029).
        let googleGescheitert = false;
        if (googleApiKey) {
          const autoRes = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': googleApiKey,
              'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text',
            },
            body: JSON.stringify({
              input: trimmed,
              languageCode: lang,
            }),
            signal: controller.signal,
          });
          if (!autoRes.ok) {
            googleGescheitert = true;
          }
          const autoData = googleGescheitert ? {} : await autoRes.json();
          const predictions = (autoData?.suggestions || [])
            .map((s: any) => s?.placePrediction)
            .filter((p: any) => p?.placeId)
            .slice(0, limit);

          const mapped = await Promise.all(
            predictions.map(async (p: any) => {
              const placeId = String(p.placeId);
              const detailsRes = await fetch(
                `https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}?languageCode=${encodeURIComponent(
                  lang
                )}&fields=id,formattedAddress,addressComponents`,
                {
                  headers: { 'X-Goog-Api-Key': googleApiKey },
                  signal: controller.signal,
                }
              );
              const details = await detailsRes.json();
              const components = Array.isArray(details?.addressComponents) ? details.addressComponents : [];
              const readComponent = (type: string) =>
                components.find((c: any) => Array.isArray(c?.types) && c.types.includes(type));

              const street = String(readComponent('route')?.longText || '');
              const houseNumber = String(readComponent('street_number')?.longText || '');
              const postalCode = String(readComponent('postal_code')?.longText || '');
              const city = String(
                readComponent('locality')?.longText ||
                  readComponent('postal_town')?.longText ||
                  readComponent('administrative_area_level_3')?.longText ||
                  ''
              );
              const country = String(readComponent('country')?.longText || '');
              const formatted = String(details?.formattedAddress || p?.text?.text || trimmed);
              const parsed = parseAddressFromFormatted(formatted);

              const structured: StructuredAddress = {
                formatted,
                street: street || parsed.street,
                houseNumber: houseNumber || parsed.houseNumber,
                postalCode: postalCode || parsed.postalCode,
                zip: postalCode || parsed.postalCode,
                city: city || parsed.city,
                country: country || parsed.country,
                placeId,
                provider: 'google_places',
              };
              return {
                id: placeId,
                label: formatted,
                structured,
              } as GeoapifySuggestion;
            })
          );

          if (!googleGescheitert) {
            setSuggestions(mapped.filter((m) => !!m.structured.formatted));
            setProviderFailed(false);
          }
        }
        if ((!googleApiKey || googleGescheitert) && geoapifyApiKey) {
          const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(trimmed)}&limit=${encodeURIComponent(
            String(limit)
          )}&lang=${encodeURIComponent(lang)}&apiKey=${encodeURIComponent(geoapifyApiKey)}`;
          const res = await fetch(url, { signal: controller.signal });
          const data = await res.json();
          const feats = data?.features || [];
          const mapped: GeoapifySuggestion[] = feats.map((f: any) => {
            const p = f.properties || {};
            const parsed = parseAddressFromFormatted(String(p.formatted || p.address_line1 || p.result || ''));
            const structured: StructuredAddress = {
              formatted: p.formatted || p.address_line1 || p.result || '',
              street: p.street || parsed.street || '',
              houseNumber: p.housenumber || parsed.houseNumber || '',
              postalCode: p.postcode || parsed.postalCode || '',
              zip: p.postcode || parsed.postalCode || '',
              city: p.city || p.town || p.village || parsed.city || '',
              state: p.state || '',
              country: p.country || parsed.country || '',
              placeId: p.place_id || undefined,
              provider: 'geoapify',
              lat: p.lat,
              lon: p.lon,
            };
            const id = String(p.place_id || p.datasource_raw_id || Math.random().toString(36).slice(2));
            const label = structured.formatted || trimmed;
            return { id, label, structured };
          });
          setSuggestions(mapped);
          setProviderFailed(false);
        } else if (googleGescheitert) {
          // Kein zweiter Anbieter da — dann soll das Feld es wenigstens sagen.
          setSuggestions([]);
          setProviderFailed(true);
        } else if (!googleApiKey) {
          setSuggestions([]);
        }
      } catch {
        setSuggestions([]);
        setProviderFailed(true);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [debounceMs, enabled, geoapifyApiKey, googleApiKey, lang, limit, trimmed]);

  return {
    suggestions,
    isLoading,
    apiKeyPresent: !!(googleApiKey || geoapifyApiKey),
    providerFailed,
  };
}


